import { useEffect, useRef, useState } from 'react';
import {
  Text,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
} from '@chakra-ui/react';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop, PercentCrop } from 'react-image-crop';
import { setCanvasPreview } from '@/components/ImageCropper/utils';
import { useTranslation } from 'react-i18next';

const MIN_DIMENSION = 80;

const aspectRatioByName = {
  SQUARE: 1,
  LANDSCAPE: 16 / 9,
  PORTRAIT: 9 / 16,
  POSTS: 4 / 3,
};

export interface EditMediaModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  aspect: 'SQUARE' | 'LANDSCAPE' | 'PORTRAIT' | 'POSTS';
  image?: File;
  onSave?: (file: File) => void;
  onDelete?: VoidFunction;
}

export const EditMediaModal = ({ isOpen, onClose, onSave, onDelete, image, aspect = 'SQUARE' }: EditMediaModalProps) => {
  const { t } = useTranslation();

  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<PercentCrop>();
  const [error, setError] = useState('');

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;

    if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
      setError(`Image must be at least ${MIN_DIMENSION} x ${MIN_DIMENSION} pixels.`);
      return;
    }

    const cropWidthInPercent = (MIN_DIMENSION / width) * 100;

    const crop = makeAspectCrop(
      {
        unit: '%',
        width: cropWidthInPercent,
      },
      aspectRatioByName[aspect],
      width,
      height,
    );
    const centeredCrop = centerCrop(crop, width, height);

    setCrop(centeredCrop);
    setError(''); // limpa qualquer erro anterior
  };

  const handleSave = () => {
    if (!crop || !imgRef.current || !previewCanvasRef.current) return;

    setCanvasPreview(
      imgRef.current,
      previewCanvasRef.current,
      convertToPixelCrop(crop, imgRef.current.width, imgRef.current.height),
    );

    previewCanvasRef.current.toBlob(blob => {
      if (blob) {
        const file = new File([blob], image?.name ?? `edited-${Date.now()}.png`, {
          type: image?.type ?? 'image/png',
        });

        onSave?.(file);
      }
    });
  };

  useEffect(() => {
    if (!image) {
      setImgSrc('');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result as string);
    };
    reader.readAsDataURL(image);

    return () => {
      reader.abort();
    };
  }, [image]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('components.edit_media_modal.edit_media_modal_title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction='column' gap={4}>
              {error && <Text color='red.500'>{error}</Text>}
              {imgSrc && !error && (
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  keepSelection
                  ruleOfThirds
                  aspect={aspectRatioByName[aspect]}
                  minWidth={MIN_DIMENSION}
                >
                  <img ref={imgRef} src={imgSrc} alt='Upload' style={{ maxHeight: '70vh' }} onLoad={onImageLoad} />
                </ReactCrop>
              )}
              <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
            </Flex>
          </ModalBody>

          <ModalFooter justifyContent='space-between' gap='kilo'>
            <Flex w='100%' justifyContent='space-between' alignItems='center' gap={2} paddingLeft={6}>
              <Button
                display='flex'
                alignItems='center'
                gap={2}
                onClick={onDelete}
                width={{ base: '100%', md: 'initial' }}
                p={0}
                color='brand.500'
                backgroundColor='transparent'
                textDecoration='underline'
                _hover={{
                  backgroundColor: 'transparent',
                  color: 'brand.500',
                }}
              >
                <ChakraFeatherIcon name='trash-2' />
                {t('components.edit_media_modal.edit_media_modal_delete_button')}
              </Button>

              <Flex w='100%' justifyContent='end' gap={5}>
                <Button variant='outlined' color='gray.600' onClick={onClose}>
                  {t('components.edit_media_modal.edit_media_modal_cancel_button')}
                </Button>
                <Button onClick={handleSave} paddingX={10}>
                  {t('components.edit_media_modal.edit_media_modal_save_button')}
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={false} />
    </>
  );
};
